import { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../services/dashboardService';
import { SchoolAdminDashboardData, TeacherDashboardData } from '../types/dashboardTypes';

interface UseDashboardReturn<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
    refetch: () => void;
}

/**
 * Custom hook for fetching School Admin Dashboard data
 * Optimized with useCallback to prevent unnecessary re-renders
 */
export const useSchoolAdminDashboard = (): UseDashboardReturn<SchoolAdminDashboardData> => {
    const [data, setData] = useState<SchoolAdminDashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await dashboardService.getSchoolAdminDashboard();
            if (response.success) {
                setData(response.data);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err: any) {
            console.error('Dashboard fetch error:', err);
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch: fetchData
    };
};

/**
 * Custom hook for fetching Teacher Dashboard data
 * Returns teacher-specific widgets: attendance, leave balance, schedule, pending marks
 * Optimized with useCallback to prevent unnecessary re-renders
 */
export const useTeacherDashboard = (): UseDashboardReturn<TeacherDashboardData> => {
    const [data, setData] = useState<TeacherDashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await dashboardService.getTeacherDashboard();
            if (response.success) {
                setData(response.data);
            } else {
                setError('Failed to load dashboard data');
            }
        } catch (err: any) {
            console.error('Teacher Dashboard fetch error:', err);
            setError(err.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return {
        data,
        loading,
        error,
        refetch: fetchData
    };
};

export default useSchoolAdminDashboard;
